import { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';

export interface TranscriptSegment {
  id: string;
  timestamp: string;
  text: string;
  speaker?: string;
  confidence?: number;
}

interface TranscriptContextType {
  transcripts: TranscriptSegment[];
  isListening: boolean;
  isMicPaused: boolean;
  addTranscript: (text: string, speaker?: string, confidence?: number) => void;
  clearTranscripts: () => void;
  startListening: () => void;
  stopListening: () => void;
  setMicPaused: (paused: boolean) => void;
}

const TranscriptContext = createContext<TranscriptContextType | undefined>(undefined);

export function TranscriptProvider({ children }: { children: ReactNode }) {
  const [transcripts, setTranscripts] = useState<TranscriptSegment[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isMicPaused, setIsMicPaused] = useState(false);

  const addTranscript = useCallback((text: string, speaker?: string, confidence?: number) => {
    if (!text || text.trim() === '') return; // Skip empty transcriptions

    const newTranscript: TranscriptSegment = {
      id: `transcript-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toLocaleTimeString(),
      text: text.trim(),
      speaker,
      confidence,
    };

    setTranscripts((prev) => [...prev, newTranscript]);
    console.log('[Transcript] Added:', newTranscript);
  }, []);

  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
    console.log('[Transcript] Cleared all transcripts');
  }, []);

  const startListening = useCallback(() => {
    setIsListening(true);
    console.log('[Transcript] Started listening');
  }, []);

  const stopListening = useCallback(() => {
    setIsListening(false);
    setIsMicPaused(false); // always clear pause when listening fully stops
    console.log('[Transcript] Stopped listening');
  }, []);

  const setMicPaused = useCallback((paused: boolean) => {
    setIsMicPaused(paused);
    console.log('[Transcript] Mic paused:', paused);
  }, []);

  return (
    <TranscriptContext.Provider
      value={{
        transcripts,
        isListening,
        isMicPaused,
        addTranscript,
        clearTranscripts,
        startListening,
        stopListening,
        setMicPaused,
      }}
    >
      {children}
    </TranscriptContext.Provider>
  );
}

export function useTranscript() {
  const context = useContext(TranscriptContext);
  if (context === undefined) {
    throw new Error('useTranscript must be used within a TranscriptProvider');
  }
  return context;
}
